<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import PageLayout from 'dashboard/components-next/pilot/PageLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/pilot/pageComponents/BulkDeleteDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PendingFaqPageEmptyState from 'dashboard/components-next/pilot/pageComponents/emptyStates/PendingFaqPageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';
import PendingFaqsFilter from 'dashboard/components-next/filter/PendingFaqsFilter.vue';
import PendingFaqDrawer from 'dashboard/components-next/pilot/pageComponents/pendingFaq/PendingFaqDrawer.vue';
import CreateFaqDrawer from 'dashboard/components-next/pilot/pageComponents/pendingFaq/CreateFaqDrawer.vue';

// 导入 SVG 图片
import faqsPopoverLight from 'assets/images/dashboard/pilot/faqs-popover-light.svg';
import faqsPopoverDark from 'assets/images/dashboard/pilot/faqs-popover-dark.svg';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();
const { isOnChatwootCloud } = useAccount();

const uiFlags = useMapGetter('pilotPendingFaqs/getUIFlags');
const pendingFaqs = useMapGetter('pilotPendingFaqs/getRecords');
const pendingFaqsMeta = useMapGetter('pilotPendingFaqs/getMeta');

// Tab 状态
const currentStatus = computed(() => route.params.status || 'pending');
const historyStatuses = ['history', 'approved', 'rejected'];
const isHistoryTab = computed(() => historyStatuses.includes(currentStatus.value));

const statusTabs = computed(() => [
  { label: t('PILOT.PENDING_FAQS.STATUS.PENDING'), value: 'pending' },
  { label: t('PILOT.PENDING_FAQS.STATUS.HISTORY'), value: 'history' },
]);

const activeStatusTabIndex = computed(() => {
  return isHistoryTab.value ? 1 : 0;
});

const handleTabChanged = tab => {
  searchValue.value = '';
  appliedFilters.value = [];
  router.push({
    name: 'pilot_pending_faqs_status',
    params: { status: tab.value },
  });
};

// 根据当前 tab 过滤数据（后端已过滤，直接使用）
const pendingFaqsArray = computed(() => pendingFaqs.value || []);

// 监听 tab 切换，重新获取数据
watch(
  () => route.params.status,
  () => {
    bulkSelectedIds.value = new Set();
    fetchPendingFaqs(1);
  }
);

const isFetching = computed(() => uiFlags.value.fetchingList);
const isEmpty = computed(() => !pendingFaqsArray.value.length);
const isSearching = computed(() => searchValue.value.trim() !== '');
const isFiltering = computed(() => hasAppliedFilters.value);
const showEmptyState = computed(
  () => isEmpty.value && !isSearching.value && !isFiltering.value
);
const showNoResults = computed(
  () => isEmpty.value && (isSearching.value || isFiltering.value)
);

// 搜索和排序状态
const searchValue = ref('');
const sortBy = ref('created_at');
// 审核历史子 tab
const historySubTabs = computed(() => [
  { label: t('PILOT.PENDING_FAQS.HISTORY_FILTER.ALL'), value: 'history' },
  { label: t('PILOT.PENDING_FAQS.HISTORY_FILTER.APPROVED'), value: 'approved' },
  { label: t('PILOT.PENDING_FAQS.HISTORY_FILTER.REJECTED'), value: 'rejected' },
]);

const activeHistorySubTabIndex = computed(() => {
  const status = route.params.status || 'pending';
  const index = historySubTabs.value.findIndex(t => t.value === status);
  return index >= 0 ? index : 0;
});

const handleHistorySubTabChanged = tab => {
  searchValue.value = '';
  appliedFilters.value = [];
  router.push({
    name: 'pilot_pending_faqs_status',
    params: { status: tab.value },
  });
};
const sortOrder = ref('-');

// 排序弹窗
const isSortMenuOpen = ref(false);

// 筛选状态
const isFilterOpen = ref(false);
const appliedFilters = ref([]);

// 抽屉
const drawerRef = ref(null);
const createFaqDrawerRef = ref(null);
const selectedPendingFaqForDrawer = ref(null);
const selectedPendingFaqForCreate = ref(null);
const filterQuery = ref([]);

let filterIdCounter = 0;
const DEFAULT_FILTER = {
  attributeKey: 'question',
  filterOperator: 'contains',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const hasAppliedFilters = computed(() => appliedFilters.value.length > 0);

// 批量选择
const bulkSelectedIds = ref(new Set());

const selectedCountLabel = computed(() => {
  return t('PILOT.PENDING_FAQS.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const buildSelectedCountLabel = computed(() => {
  const count = pendingFaqsArray.value.length;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('PILOT.PENDING_FAQS.UNSELECT_ALL', { count })
    : t('PILOT.PENDING_FAQS.SELECT_ALL', { count });
});

const handleCardSelect = (id, isSelected) => {
  const selected = new Set(bulkSelectedIds.value);
  if (isSelected) {
    selected.add(id);
  } else {
    selected.delete(id);
  }
  bulkSelectedIds.value = selected;
};

// 排序选项
const sortMenus = computed(() => [
  {
    label: t('PILOT.PENDING_FAQS.SORT.CREATED_AT'),
    value: 'created_at',
  },
  {
    label: t('PILOT.PENDING_FAQS.SORT.UPDATED_AT'),
    value: 'updated_at',
  },
]);

// 排序顺序选项
const orderingMenus = computed(() => [
  {
    label: t('PILOT.PENDING_FAQS.SORT.ASCENDING'),
    value: '',
  },
  {
    label: t('PILOT.PENDING_FAQS.SORT.DESCENDING'),
    value: '-',
  },
]);

const activeSortLabel = computed(() => {
  const selectedMenu = sortMenus.value.find(
    menu => menu.value === sortBy.value
  );
  return selectedMenu?.label || t('PILOT.PENDING_FAQS.SORT.CREATED_AT');
});

const activeOrderingLabel = computed(() => {
  const selectedMenu = orderingMenus.value.find(
    menu => menu.value === sortOrder.value
  );
  return selectedMenu?.label || t('PILOT.PENDING_FAQS.SORT.DESCENDING');
});

// 获取列表
const fetchPendingFaqs = (page = 1) => {
  // 直接使用路由参数作为 status
  const status = route.params.status || 'pending';

  // 如果有筛选条件，使用 filter API
  if (hasAppliedFilters.value) {
    const sortAttr =
      sortOrder.value === '-' ? `-${sortBy.value}` : sortBy.value;
    const snakeCaseFilters = useSnakeCase(
      JSON.parse(JSON.stringify(appliedFilters.value))
    );
    const queryPayload = filterQueryGenerator(snakeCaseFilters);
    store.dispatch('pilotPendingFaqs/filter', {
      page,
      sortAttr,
      queryPayload,
      search: searchValue.value,
      status,
    });
    return;
  }

  // 否则使用常规的 get API
  const params = { page, status };

  if (searchValue.value) {
    params.search = searchValue.value;
  }

  if (sortBy.value) {
    const sortPrefix = sortOrder.value === '-' ? '-' : '';
    params.sort = `${sortPrefix}${sortBy.value}`;
  }

  store.dispatch('pilotPendingFaqs/get', params);
};

// 搜索防抖
let searchDebounceTimer = null;
const handleSearchInput = event => {
  searchValue.value = event.target.value;
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    fetchPendingFaqs(1);
  }, 500);
};

const handleSortChange = value => {
  sortBy.value = value;
  fetchPendingFaqs(1);
};

const handleOrderChange = value => {
  sortOrder.value = value;
  fetchPendingFaqs(1);
};

const closeSortMenu = () => {
  isSortMenuOpen.value = false;
};

// 筛选相关
const handleToggleFilter = () => {
  isFilterOpen.value = !isFilterOpen.value;
  if (isFilterOpen.value && filterQuery.value.length === 0) {
    filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  }
};

const handleCloseFilter = () => {
  isFilterOpen.value = false;
};

const handleClearFilters = () => {
  appliedFilters.value = [];
  filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  fetchPendingFaqs(1);
};

const handleApplyFilter = filters => {
  appliedFilters.value = filters;
  fetchPendingFaqs(1);
  isFilterOpen.value = false;
};

// 分页
const onPageChange = page => fetchPendingFaqs(page);

// 审核通过
const selectedPendingFaq = ref(null);
const knowledgeBases = useMapGetter('pilotKnowledgeBases/getRecords');
const knowledgeBasesArray = computed(() => knowledgeBases.value || []);

const handleApprove = pendingFaq => {
  selectedPendingFaqForCreate.value = pendingFaq;
  createFaqDrawerRef.value?.open();
};

// 打开抽屉查看详情
const handleOpenDrawer = pendingFaq => {
  selectedPendingFaqForDrawer.value = pendingFaq;
  drawerRef.value?.open();
};

// 抽屉关闭后刷新列表
const handleDrawerClose = () => {
  selectedPendingFaqForDrawer.value = null;
};

const handleCreateFaqClose = () => {
  selectedPendingFaqForCreate.value = null;
};

const handleCreateFaqCreated = () => {
  fetchPendingFaqs();
};

// 拒绝弹窗
const rejectDialogRef = ref(null);
const rejectNote = ref('');

const handleReject = pendingFaq => {
  selectedPendingFaq.value = pendingFaq;
  rejectNote.value = '';
  rejectDialogRef.value?.open();
};

const confirmReject = async () => {
  try {
    await store.dispatch('pilotPendingFaqs/reject', {
      id: selectedPendingFaq.value.id,
      note: rejectNote.value,
    });
    useAlert(t('PILOT.PENDING_FAQS.REJECT.SUCCESS_MESSAGE'));
    fetchPendingFaqs();
    rejectDialogRef.value?.close();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.PENDING_FAQS.REJECT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 删除
const deleteDialogRef = ref(null);

const handleDelete = pendingFaq => {
  selectedPendingFaq.value = pendingFaq;
  deleteDialogRef.value?.dialogRef?.open();
};

const onDeleteSuccess = () => {
  fetchPendingFaqs();
};

// 批量操作
const bulkRejectDialogRef = ref(null);
const bulkRejectNote = ref('');

const handleBulkReject = () => {
  bulkRejectNote.value = '';
  bulkRejectDialogRef.value?.open();
};

const confirmBulkReject = async () => {
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await store.dispatch('pilotPendingFaqs/batchReject', {
      ids,
      note: bulkRejectNote.value,
    });
    useAlert(
      t('PILOT.PENDING_FAQS.BULK_REJECT.SUCCESS_MESSAGE', { count: ids.length })
    );
    bulkSelectedIds.value = new Set();
    bulkRejectDialogRef.value?.close();
    fetchPendingFaqs();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.PENDING_FAQS.BULK_REJECT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const bulkDeleteDialogRef = ref(null);
const bulkApproveDialogRef = ref(null);
const bulkApproveKnowledgeBaseId = ref(null);

const openBulkApproveDialog = () => {
  bulkApproveKnowledgeBaseId.value = null;
  bulkApproveDialogRef.value?.open();
};

const handleBulkDelete = () => {
  bulkDeleteDialogRef.value?.dialogRef?.open();
};

const onBulkDeleteSuccess = () => {
  bulkSelectedIds.value = new Set();
  fetchPendingFaqs();
};

// 批量通过确认
const confirmBulkApprove = async () => {
  if (!bulkApproveKnowledgeBaseId.value) {
    useAlert('请选择目标知识库');
    return;
  }
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await store.dispatch('pilotPendingFaqs/batchApprove', {
      ids,
      knowledgeBaseId: bulkApproveKnowledgeBaseId.value,
    });
    useAlert(
      t('PILOT.PENDING_FAQS.BULK_APPROVE.SUCCESS_MESSAGE', {
        count: ids.length,
      })
    );
    bulkSelectedIds.value = new Set();
    bulkApproveDialogRef.value?.close();
    fetchPendingFaqs();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.PENDING_FAQS.BULK_APPROVE.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 重置为待审核弹窗（已通过）
const resetToPendingDialogRef = ref(null);

const handleResetToPending = pendingFaq => {
  selectedPendingFaq.value = pendingFaq;
  resetToPendingDialogRef.value?.open();
};

const confirmResetToPending = async () => {
  try {
    await store.dispatch('pilotPendingFaqs/resetToPending', {
      id: selectedPendingFaq.value.id,
    });
    useAlert(t('PILOT.PENDING_FAQS.RESET_TO_PENDING.SUCCESS_MESSAGE'));
    fetchPendingFaqs();
    resetToPendingDialogRef.value?.close();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.PENDING_FAQS.RESET_TO_PENDING.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 重置为拒绝弹窗
const resetToRejectedDialogRef = ref(null);
const resetToRejectedNote = ref('');

const handleResetToRejected = pendingFaq => {
  selectedPendingFaq.value = pendingFaq;
  resetToRejectedNote.value = '';
  resetToRejectedDialogRef.value?.open();
};

const confirmResetToRejected = async () => {
  try {
    await store.dispatch('pilotPendingFaqs/resetToRejected', {
      id: selectedPendingFaq.value.id,
      note: resetToRejectedNote.value,
    });
    useAlert(t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.SUCCESS_MESSAGE'));
    fetchPendingFaqs();
    resetToRejectedDialogRef.value?.close();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 批量重置为待审核弹窗（已通过）
const bulkResetToPendingDialogRef = ref(null);

const handleBulkResetToPending = () => {
  bulkResetToPendingDialogRef.value?.open();
};

const confirmBulkResetToPending = async () => {
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await store.dispatch('pilotPendingFaqs/batchResetToPending', ids);
    useAlert(
      t('PILOT.PENDING_FAQS.BULK_RESET_TO_PENDING.SUCCESS_MESSAGE', {
        count: ids.length,
      })
    );
    bulkSelectedIds.value = new Set();
    bulkResetToPendingDialogRef.value?.close();
    fetchPendingFaqs();
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('PILOT.PENDING_FAQS.BULK_RESET_TO_PENDING.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 批量重置为拒绝
const bulkResetToRejectedDialogRef = ref(null);
const bulkResetToRejectedNote = ref('');

const handleBulkResetToRejected = () => {
  bulkResetToRejectedNote.value = '';
  bulkResetToRejectedDialogRef.value?.open();
};

const confirmBulkResetToRejected = async () => {
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await store.dispatch('pilotPendingFaqs/batchResetToRejected', {
      ids,
      note: bulkResetToRejectedNote.value,
    });
    useAlert(
      t('PILOT.PENDING_FAQS.BULK_RESET_TO_REJECTED.SUCCESS_MESSAGE', {
        count: ids.length,
      })
    );
    bulkSelectedIds.value = new Set();
    bulkResetToRejectedDialogRef.value?.close();
    fetchPendingFaqs();
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('PILOT.PENDING_FAQS.BULK_RESET_TO_REJECTED.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// ========== 从已拒绝状态重置的操作 ==========

// 从已拒绝重置为待审核弹窗
const resetPendingFromRejectedDialogRef = ref(null);

const handleResetPendingFromRejected = pendingFaq => {
  selectedPendingFaq.value = pendingFaq;
  resetPendingFromRejectedDialogRef.value?.open();
};

const confirmResetPendingFromRejected = async () => {
  try {
    await store.dispatch('pilotPendingFaqs/resetPendingFromRejected', {
      id: selectedPendingFaq.value.id,
    });
    useAlert(
      t('PILOT.PENDING_FAQS.RESET_PENDING_FROM_REJECTED.SUCCESS_MESSAGE')
    );
    fetchPendingFaqs();
    resetPendingFromRejectedDialogRef.value?.close();
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('PILOT.PENDING_FAQS.RESET_PENDING_FROM_REJECTED.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 从已拒绝重置为通过（打开创建FAQ抽屉）
const handleResetApprovedFromRejected = pendingFaq => {
  selectedPendingFaqForCreate.value = pendingFaq;
  createFaqDrawerRef.value?.open();
};

// 批量从已拒绝重置为待审核弹窗
const bulkResetPendingFromRejectedDialogRef = ref(null);

const handleBulkResetPendingFromRejected = () => {
  bulkResetPendingFromRejectedDialogRef.value?.open();
};

const confirmBulkResetPendingFromRejected = async () => {
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await store.dispatch('pilotPendingFaqs/batchResetPendingFromRejected', ids);
    useAlert(
      t('PILOT.PENDING_FAQS.BULK_RESET_PENDING_FROM_REJECTED.SUCCESS_MESSAGE', {
        count: ids.length,
      })
    );
    bulkSelectedIds.value = new Set();
    bulkResetPendingFromRejectedDialogRef.value?.close();
    fetchPendingFaqs();
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('PILOT.PENDING_FAQS.BULK_RESET_PENDING_FROM_REJECTED.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 批量从已拒绝重置为通过弹窗
const bulkResetApprovedFromRejectedDialogRef = ref(null);
const bulkResetApprovedFromRejectedKnowledgeBaseId = ref(null);

const handleBulkResetApprovedFromRejected = () => {
  bulkResetApprovedFromRejectedKnowledgeBaseId.value = null;
  bulkResetApprovedFromRejectedDialogRef.value?.open();
};

const confirmBulkResetApprovedFromRejected = async () => {
  if (!bulkResetApprovedFromRejectedKnowledgeBaseId.value) {
    useAlert('请选择目标知识库');
    return;
  }
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await store.dispatch('pilotPendingFaqs/batchResetApprovedFromRejected', {
      ids,
      knowledgeBaseId: bulkResetApprovedFromRejectedKnowledgeBaseId.value,
    });
    useAlert(
      t(
        'PILOT.PENDING_FAQS.BULK_RESET_APPROVED_FROM_REJECTED.SUCCESS_MESSAGE',
        { count: ids.length }
      )
    );
    bulkSelectedIds.value = new Set();
    bulkResetApprovedFromRejectedDialogRef.value?.close();
    fetchPendingFaqs();
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('PILOT.PENDING_FAQS.BULK_RESET_APPROVED_FROM_REJECTED.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 获取状态标签样式
const getStatusConfig = status => {
  const configMap = {
    pending: {
      bgColor: 'bg-n-amber-1 dark:bg-n-amber-2',
      textColor: 'text-n-amber-10 dark:text-n-amber-9',
      dotColor: 'bg-n-amber-5',
    },
    approved: {
      bgColor: 'bg-n-teal-1 dark:bg-n-teal-2',
      textColor: 'text-n-teal-10 dark:text-n-teal-9',
      dotColor: 'bg-n-teal-5',
    },
    rejected: {
      bgColor: 'bg-n-ruby-1 dark:bg-n-ruby-2',
      textColor: 'text-n-ruby-10 dark:text-n-ruby-9',
      dotColor: 'bg-n-ruby-5',
    },
  };

  return (
    configMap[status] || {
      bgColor: 'bg-n-slate-1 dark:bg-n-slate-2',
      textColor: 'text-n-slate-10 dark:text-n-slate-9',
      dotColor: 'bg-n-slate-5',
    }
  );
};

const getStatusText = status => {
  return t(`PILOT.PENDING_FAQS.STATUS.${status.toUpperCase()}`);
};

// 打开对话
const openConversation = (conversationId, inboxId) => {
  router.push({
    name: 'inbox_conversation',
    params: {
      inbox_id: inboxId,
      conversation_id: conversationId,
    },
  });
};

onMounted(() => {
  fetchPendingFaqs();
  store.dispatch('pilotKnowledgeBases/get', { page: 1 });
});
</script>

<template>
  <PageLayout
    :header-title="$t('PILOT.PENDING_FAQS.HEADER')"
    :total-count="pendingFaqsMeta?.totalCount || 0"
    :current-page="pendingFaqsMeta?.page || 1"
    :show-pagination-footer="
      !isFetching && !showEmptyState && !!pendingFaqsArray.length
    "
    :is-fetching="isFetching"
    :is-empty="showEmptyState"
    :show-assistant-switcher="false"
    @update:current-page="onPageChange"
  >
    <template #subHeader>
      <!-- Tab 切换 -->
      <TabBar
        :tabs="statusTabs"
        :initial-active-tab="activeStatusTabIndex"
        class="mb-3"
        @tab-changed="handleTabChanged"
      />

      <!-- 搜索和筛选工具条（始终显示） -->
      <div class="relative w-full mb-3">
        <div class="flex flex-wrap items-center gap-3 px-0">
          <!-- 搜索框 -->
          <div class="flex-1 min-w-[200px] max-w-md">
            <Input
              :model-value="searchValue"
              type="search"
              size="sm"
              :placeholder="$t('PILOT.PENDING_FAQS.SEARCH.PLACEHOLDER')"
              :custom-input-class="[
                '[&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 rtl:!pr-8',
              ]"
              class="w-full"
              @input="handleSearchInput"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
          </div>

          <!-- 审核历史子 tab -->
          <TabBar
            v-if="isHistoryTab"
            :tabs="historySubTabs"
            :initial-active-tab="activeHistorySubTabIndex"
            @tab-changed="handleHistorySubTabChanged"
          />

          <!-- 筛选和排序按钮 -->
          <div class="flex gap-2">
            <Button
              id="togglePendingFaqsFilterButton"
              icon="i-lucide-sliders-horizontal"
              color="slate"
              size="sm"
              variant="ghost"
              :class="{ 'bg-n-alpha-2': isFilterOpen || hasAppliedFilters }"
              @click="handleToggleFilter"
            />

            <div class="relative">
              <Button
                icon="i-lucide-arrow-down-up"
                color="slate"
                size="sm"
                variant="ghost"
                :class="isSortMenuOpen ? 'bg-n-alpha-2' : ''"
                @click="isSortMenuOpen = !isSortMenuOpen"
              />

              <!-- 排序弹窗 -->
              <div
                v-if="isSortMenuOpen"
                v-on-clickaway="closeSortMenu"
                class="absolute top-full mt-1 ltr:-right-32 rtl:-left-32 sm:ltr:right-0 sm:rtl:left-0 flex flex-col gap-4 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-xl p-4 z-20"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="text-sm text-n-slate-12">
                    {{ t('PILOT.PENDING_FAQS.SORT.SORT_BY_LABEL') }}
                  </span>
                  <SelectMenu
                    :model-value="sortBy"
                    :options="sortMenus"
                    :label="activeSortLabel"
                    @update:model-value="handleSortChange"
                  />
                </div>
                <div class="flex items-center justify-between gap-2">
                  <span class="text-sm text-n-slate-12">
                    {{ t('PILOT.PENDING_FAQS.SORT.ORDER_LABEL') }}
                  </span>
                  <SelectMenu
                    :model-value="sortOrder"
                    :options="orderingMenus"
                    :label="activeOrderingLabel"
                    @update:model-value="handleOrderChange"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 筛选弹窗 -->
        <div v-if="isFilterOpen" class="absolute top-full left-0 z-50 mt-2">
          <PendingFaqsFilter
            v-model="filterQuery"
            @apply-filter="handleApplyFilter"
            @close="handleCloseFilter"
            @clear-filters="handleClearFilters"
          />
        </div>
      </div>

      <!-- 以下仅在有数据时显示 -->
      <template v-if="!showEmptyState">
        <!-- 批量操作条（待审核状态） -->
        <BulkSelectBar
          v-if="(route.params.status || 'pending') === 'pending'"
          v-model="bulkSelectedIds"
          :all-items="pendingFaqsArray"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          class="w-full"
        >
          <template #actions>
            <div class="flex items-center gap-2">
              <Button
                :label="$t('PILOT.PENDING_FAQS.BULK_APPROVE.BUTTON')"
                icon="i-lucide-check"
                size="sm"
                ghost
                blue
                @click="openBulkApproveDialog"
              />
              <Button
                :label="$t('PILOT.PENDING_FAQS.BULK_REJECT.BUTTON')"
                icon="i-lucide-x"
                size="sm"
                ghost
                blue
                @click="handleBulkReject"
              />
              <Button
                :label="$t('PILOT.PENDING_FAQS.BULK_DELETE.BUTTON')"
                icon="i-lucide-trash"
                size="sm"
                ghost
                blue
                @click="handleBulkDelete"
              />
            </div>
          </template>
        </BulkSelectBar>

        <!-- 批量操作条（审核历史） -->
        <BulkSelectBar
          v-if="isHistoryTab"
          v-model="bulkSelectedIds"
          :all-items="pendingFaqsArray"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          class="w-full"
        >
          <template #actions>
            <div class="flex items-center gap-2">
              <Button
                :label="$t('PILOT.PENDING_FAQS.BULK_RESET_TO_PENDING.BUTTON')"
                icon="i-lucide-rotate-ccw"
                size="sm"
                ghost
                blue
                @click="handleBulkResetToPending"
              />
              <Button
                :label="$t('PILOT.PENDING_FAQS.BULK_DELETE.BUTTON')"
                icon="i-lucide-trash"
                size="sm"
                ghost
                blue
                @click="handleBulkDelete"
              />
            </div>
          </template>
        </BulkSelectBar>

        <!-- 智能去重提示 -->
        <div
          v-show="bulkSelectedIds.size === 0"
          class="flex items-center gap-2 px-3 py-2 mb-2 text-sm text-n-slate-11 dark:text-n-slate-5 bg-n-slate-2 dark:bg-n-slate-3 rounded-lg"
        >
          <Icon icon="i-ph-sparkle-fill" class="size-4 text-n-brand" />
          <span>{{ $t('PILOT.PENDING_FAQS.DEDUCTION_TIP') }}</span>
        </div>
      </template>
    </template>

    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('PILOT.HEADER_KNOW_MORE')"
        :title="$t('PILOT.PENDING_FAQS.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('PILOT.PENDING_FAQS.FEATURE_SPOTLIGHT.NOTE')"
        :hide-actions="!isOnChatwootCloud"
        :fallback-thumbnail="faqsPopoverLight"
        :fallback-thumbnail-dark="faqsPopoverDark"
      />
    </template>

    <template #emptyState>
      <PendingFaqPageEmptyState />
    </template>

    <template #body>
      <!-- Loading -->
      <div v-if="isFetching" class="flex items-center justify-center py-16">
        <Spinner />
      </div>

      <!-- 搜索/筛选无结果提示 -->
      <div
        v-else-if="showNoResults"
        class="flex flex-col items-center justify-center py-16 text-center"
      >
        <div
          class="i-lucide-search-x text-6xl text-n-slate-7 dark:text-n-slate-4 mb-4"
        />
        <h3
          class="text-xl font-semibold text-n-slate-12 dark:text-n-slate-1 mb-2"
        >
          {{ t('PILOT.PENDING_FAQS.SEARCH.NO_RESULTS') }}
        </h3>
        <p class="text-n-slate-11 dark:text-n-slate-5">
          {{ t('PILOT.PENDING_FAQS.SEARCH.TRY_DIFFERENT_KEYWORDS') }}
        </p>
        <button
          v-if="isFiltering"
          class="mt-4 px-4 py-2 bg-n-blue-6 text-white rounded-lg hover:bg-n-blue-7 transition-colors"
          @click="handleClearFilters"
        >
          {{ t('PILOT.PENDING_FAQS.FILTER.CLEAR_FILTERS') }}
        </button>
        <button
          v-else
          class="mt-4 px-4 py-2 bg-n-blue-6 text-white rounded-lg hover:bg-n-blue-7 transition-colors"
          @click="
            searchValue = '';
            fetchPendingFaqs(1);
          "
        >
          {{ t('PILOT.PENDING_FAQS.SEARCH.CLEAR_SEARCH') }}
        </button>
      </div>

      <!-- 列表 -->
      <div v-else class="space-y-3">
        <div
          v-for="item in pendingFaqsArray"
          :key="item.id"
          class="p-4 border border-n-container rounded-xl bg-n-slate-white dark:bg-n-slate-900 hover:border-n-slate-6 dark:hover:border-n-slate-7 transition-colors"
        >
          <div class="flex items-start justify-between gap-4">
            <!-- 左侧：选择框 + 内容 -->
            <div class="flex items-start gap-3 flex-1">
              <input
                type="checkbox"
                :checked="bulkSelectedIds.has(item.id)"
                :disabled="
                  item.status !== 'pending' &&
                  item.status !== 'approved' &&
                  item.status !== 'rejected'
                "
                class="mt-1 h-4 w-4 rounded border-n-slate-6 text-n-brand focus:ring-n-brand"
                @change="e => handleCardSelect(item.id, e.target.checked)"
              />
              <div
                class="flex-1 min-w-0 cursor-pointer"
                @click="handleOpenDrawer(item)"
              >
                <!-- 问题 -->
                <h4
                  class="text-sm font-medium text-n-slate-12 dark:text-n-slate-1 mb-1 hover:text-n-blue-11 transition-colors"
                >
                  {{ item.question }}
                </h4>
                <!-- 答案 -->
                <p
                  class="text-sm text-n-slate-10 dark:text-n-slate-4 line-clamp-2 mb-2"
                >
                  {{ item.answer }}
                </p>
                <!-- 元信息 -->
                <div
                  class="flex items-center gap-3 text-xs text-n-slate-9 dark:text-n-slate-5"
                >
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="[
                      getStatusConfig(item.status).bgColor,
                      getStatusConfig(item.status).textColor,
                    ]"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :class="getStatusConfig(item.status).dotColor"
                    />
                    {{ getStatusText(item.status) }}
                  </span>
                  <button
                    v-if="item.source_conversation"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-n-slate-2 dark:bg-n-slate-3 hover:bg-n-slate-3 dark:hover:bg-n-slate-4 text-n-slate-11 dark:text-n-slate-5 hover:text-n-brand dark:hover:text-n-brand transition-colors cursor-pointer"
                    @click.stop="
                      openConversation(
                        item.source_conversation.id,
                        item.source_conversation.inbox_id
                      )
                    "
                  >
                    <Icon icon="i-lucide-message-square" class="size-3" />
                    <span>对话 #{{ item.source_conversation.display_id }}</span>
                  </button>
                  <span>{{ dynamicTime(item.created_at) }}</span>
                </div>
              </div>
            </div>

            <!-- 右侧：操作按钮 -->
            <div
              v-if="item.status === 'pending'"
              class="flex items-center gap-2"
            >
              <Button
                :label="$t('PILOT.PENDING_FAQS.APPROVE.BUTTON')"
                icon="i-lucide-check"
                size="sm"
                solid
                blue
                @click="handleApprove(item)"
              />
              <Button
                :label="$t('PILOT.PENDING_FAQS.REJECT.BUTTON')"
                icon="i-lucide-x"
                size="sm"
                ghost
                @click="handleReject(item)"
              />
              <Button
                icon="i-lucide-trash"
                size="sm"
                ghost
                @click="handleDelete(item)"
              />
            </div>

            <!-- 已通过状态的操作按钮 -->
            <div
              v-if="item.status === 'approved'"
              class="flex items-center gap-2"
            >
              <Button
                :label="$t('PILOT.PENDING_FAQS.RESET_TO_PENDING.BUTTON')"
                icon="i-lucide-rotate-ccw"
                size="sm"
                ghost
                blue
                @click="handleResetToPending(item)"
              />
              <Button
                :label="$t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.BUTTON')"
                icon="i-lucide-x"
                size="sm"
                ghost
                @click="handleResetToRejected(item)"
              />
              <Button
                icon="i-lucide-trash"
                size="sm"
                ghost
                @click="handleDelete(item)"
              />
            </div>

            <!-- 已拒绝状态的操作按钮 -->
            <div
              v-if="item.status === 'rejected'"
              class="flex items-center gap-2"
            >
              <Button
                :label="
                  $t('PILOT.PENDING_FAQS.RESET_PENDING_FROM_REJECTED.BUTTON')
                "
                icon="i-lucide-rotate-ccw"
                size="sm"
                ghost
                blue
                @click="handleResetPendingFromRejected(item)"
              />
              <Button
                :label="
                  $t('PILOT.PENDING_FAQS.RESET_APPROVED_FROM_REJECTED.BUTTON')
                "
                icon="i-lucide-check"
                size="sm"
                solid
                blue
                @click="handleResetApprovedFromRejected(item)"
              />
              <Button
                icon="i-lucide-trash"
                size="sm"
                ghost
                @click="handleDelete(item)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>

  <!-- 删除弹窗 -->
  <DeleteDialog
    v-if="selectedPendingFaq"
    ref="deleteDialogRef"
    type="PendingFaq"
    translation-key="PENDING_FAQS"
    :entity="selectedPendingFaq"
    @delete-success="onDeleteSuccess"
  />

  <!-- 批量删除弹窗 -->
  <BulkDeleteDialog
    ref="bulkDeleteDialogRef"
    :bulk-ids="Array.from(bulkSelectedIds)"
    type="PendingFaqs"
    translation-key="PENDING_FAQS"
    @delete-success="onBulkDeleteSuccess"
  />

  <!-- 详情抽屉（只读查看） -->
  <PendingFaqDrawer
    ref="drawerRef"
    :pending-faq="selectedPendingFaqForDrawer"
    @close="handleDrawerClose"
    @approve="handleApprove"
    @reject="handleReject"
    @delete="handleDelete"
  />

  <!-- 创建 FAQ 抽屉 -->
  <CreateFaqDrawer
    ref="createFaqDrawerRef"
    :pending-faq="selectedPendingFaqForCreate"
    @close="handleCreateFaqClose"
    @created="handleCreateFaqCreated"
  />

  <!-- 拒绝弹窗 -->
  <Dialog
    ref="rejectDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.REJECT.TITLE')"
    :confirm-button-label="$t('PILOT.PENDING_FAQS.REJECT.BUTTON')"
    @confirm="confirmReject"
  >
    <div class="mb-2">
      <label
        class="block text-sm font-medium text-n-slate-11 dark:text-n-slate-5 mb-2"
      >
        {{ $t('PILOT.PENDING_FAQS.REJECT.NOTE_LABEL') }}
      </label>
      <textarea
        v-model="rejectNote"
        :placeholder="$t('PILOT.PENDING_FAQS.REJECT.NOTE_PLACEHOLDER')"
        rows="3"
        class="w-full px-3 py-2 text-sm border border-n-container rounded-lg bg-n-slate-white dark:bg-n-slate-900 resize-none"
      />
    </div>
  </Dialog>

  <!-- 批量通过弹窗 -->
  <Dialog
    ref="bulkApproveDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.BULK_APPROVE.TITLE')"
    :confirm-button-label="$t('PILOT.PENDING_FAQS.BULK_APPROVE.BUTTON')"
    @confirm="confirmBulkApprove"
  >
    <div class="mb-2">
      <label
        class="block text-sm font-medium text-n-slate-11 dark:text-n-slate-5 mb-2"
      >
        {{ $t('PILOT.PENDING_FAQS.BULK_APPROVE.SELECT_KB_LABEL') }}
      </label>
      <ComboBox
        v-model="bulkApproveKnowledgeBaseId"
        :options="
          knowledgeBasesArray.map(kb => ({ label: kb.name, value: kb.id }))
        "
        :placeholder="$t('PILOT.PENDING_FAQS.APPROVE.SELECT_KB_PLACEHOLDER')"
      />
    </div>
  </Dialog>

  <!-- 批量拒绝弹窗 -->
  <Dialog
    ref="bulkRejectDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.BULK_REJECT.TITLE')"
    :confirm-button-label="$t('PILOT.PENDING_FAQS.BULK_REJECT.BUTTON')"
    @confirm="confirmBulkReject"
  >
    <div class="mb-2">
      <label
        class="block text-sm font-medium text-n-slate-11 dark:text-n-slate-5 mb-2"
      >
        {{ $t('PILOT.PENDING_FAQS.REJECT.NOTE_LABEL') }}
      </label>
      <textarea
        v-model="bulkRejectNote"
        :placeholder="$t('PILOT.PENDING_FAQS.REJECT.NOTE_PLACEHOLDER')"
        rows="3"
        class="w-full px-3 py-2 text-sm border border-n-container rounded-lg bg-n-slate-white dark:bg-n-slate-900 resize-none"
      />
    </div>
  </Dialog>

  <!-- 重置为拒绝弹窗 -->
  <Dialog
    ref="resetToRejectedDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.TITLE')"
    :description="$t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.DESCRIPTION')"
    :confirm-button-label="$t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.BUTTON')"
    @confirm="confirmResetToRejected"
  >
    <div class="mb-2">
      <label
        class="block text-sm font-medium text-n-slate-11 dark:text-n-slate-5 mb-2"
      >
        {{ $t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.NOTE_LABEL') }}
      </label>
      <textarea
        v-model="resetToRejectedNote"
        :placeholder="
          $t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.NOTE_PLACEHOLDER')
        "
        rows="3"
        class="w-full px-3 py-2 text-sm border border-n-container rounded-lg bg-n-slate-white dark:bg-n-slate-900 resize-none"
      />
    </div>
  </Dialog>

  <!-- 单条重置为待审核确认弹窗（已通过） -->
  <Dialog
    ref="resetToPendingDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.RESET_TO_PENDING.TITLE')"
    :description="$t('PILOT.PENDING_FAQS.RESET_TO_PENDING.DESCRIPTION')"
    :confirm-button-label="$t('PILOT.PENDING_FAQS.RESET_TO_PENDING.BUTTON')"
    @confirm="confirmResetToPending"
  />

  <!-- 批量重置为待审核确认弹窗（已通过） -->
  <Dialog
    ref="bulkResetToPendingDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.BULK_RESET_TO_PENDING.TITLE')"
    :description="$t('PILOT.PENDING_FAQS.BULK_RESET_TO_PENDING.DESCRIPTION')"
    :confirm-button-label="
      $t('PILOT.PENDING_FAQS.BULK_RESET_TO_PENDING.BUTTON')
    "
    @confirm="confirmBulkResetToPending"
  />

  <!-- 单条重置为待审核确认弹窗（已拒绝） -->
  <Dialog
    ref="resetPendingFromRejectedDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.RESET_PENDING_FROM_REJECTED.TITLE')"
    :description="
      $t('PILOT.PENDING_FAQS.RESET_PENDING_FROM_REJECTED.DESCRIPTION')
    "
    :confirm-button-label="
      $t('PILOT.PENDING_FAQS.RESET_PENDING_FROM_REJECTED.BUTTON')
    "
    @confirm="confirmResetPendingFromRejected"
  />

  <!-- 批量重置为待审核确认弹窗（已拒绝） -->
  <Dialog
    ref="bulkResetPendingFromRejectedDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.BULK_RESET_PENDING_FROM_REJECTED.TITLE')"
    :description="
      $t('PILOT.PENDING_FAQS.BULK_RESET_PENDING_FROM_REJECTED.DESCRIPTION')
    "
    :confirm-button-label="
      $t('PILOT.PENDING_FAQS.BULK_RESET_PENDING_FROM_REJECTED.BUTTON')
    "
    @confirm="confirmBulkResetPendingFromRejected"
  />

  <!-- 批量重置为拒绝弹窗 -->
  <Dialog
    ref="bulkResetToRejectedDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.BULK_RESET_TO_REJECTED.TITLE')"
    :description="$t('PILOT.PENDING_FAQS.BULK_RESET_TO_REJECTED.DESCRIPTION')"
    :confirm-button-label="
      $t('PILOT.PENDING_FAQS.BULK_RESET_TO_REJECTED.BUTTON')
    "
    @confirm="confirmBulkResetToRejected"
  >
    <div class="mb-2">
      <label
        class="block text-sm font-medium text-n-slate-11 dark:text-n-slate-5 mb-2"
      >
        {{ $t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.NOTE_LABEL') }}
      </label>
      <textarea
        v-model="bulkResetToRejectedNote"
        :placeholder="
          $t('PILOT.PENDING_FAQS.RESET_TO_REJECTED.NOTE_PLACEHOLDER')
        "
        rows="3"
        class="w-full px-3 py-2 text-sm border border-n-container rounded-lg bg-n-slate-white dark:bg-n-slate-900 resize-none"
      />
    </div>
  </Dialog>

  <!-- 批量从已拒绝重置为通过弹窗 -->
  <Dialog
    ref="bulkResetApprovedFromRejectedDialogRef"
    type="alert"
    :title="$t('PILOT.PENDING_FAQS.BULK_RESET_APPROVED_FROM_REJECTED.TITLE')"
    :confirm-button-label="
      $t('PILOT.PENDING_FAQS.BULK_RESET_APPROVED_FROM_REJECTED.BUTTON')
    "
    @confirm="confirmBulkResetApprovedFromRejected"
  >
    <div class="mb-2">
      <label
        class="block text-sm font-medium text-n-slate-11 dark:text-n-slate-5 mb-2"
      >
        {{ $t('PILOT.PENDING_FAQS.BULK_APPROVE.SELECT_KB_LABEL') }}
      </label>
      <ComboBox
        v-model="bulkResetApprovedFromRejectedKnowledgeBaseId"
        :options="
          knowledgeBasesArray.map(kb => ({ label: kb.name, value: kb.id }))
        "
        :placeholder="$t('PILOT.PENDING_FAQS.APPROVE.SELECT_KB_PLACEHOLDER')"
      />
    </div>
  </Dialog>
</template>
